import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Download, 
  Search, 
  TrendingUp, 
  ShieldCheck, 
  Clock,
  CheckCircle2,
  LogOut,
  ChevronRight,
  UserPlus,
  ArrowRight,
  UserCheck,
  LayoutDashboard,
  MoreHorizontal,
  X,
  UserCircle,
  Zap,
  Tag,
  Key,
  Mail,
  Smartphone,
  MapPin,
  CreditCard,
  Building,
  Hash
} from 'lucide-react';
import { apiService } from '../services/api';

const AVAILABLE_AGENTS = [
  { name: "Kenneth Omondi Ajowi", phone: "702516131", code: "AG100104" },
  { name: "Juliet Ochieng", phone: "712353144", code: "AG100105" },
  { name: "Maria Utungo Kivuva", phone: "715916465", code: "AG100106" },
  { name: "Miriam Maweu", phone: "741719945", code: "AG100107" },
  { name: "Yvonne Muchiri", phone: "724988511", code: "AG100108" }
];

interface PortalAdminProps {
  user: { name: string; phone: string; role: string };
  onLogout: () => void;
}

const PortalAdmin: React.FC<PortalAdminProps> = ({ user, onLogout }) => {
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [applications, setApplications] = useState<any[]>([]);
  const [filterStatus, setFilterStatus] = useState('All');
  const [assignmentModal, setAssignmentModal] = useState<{ open: boolean; appId: string | null; appName: string | null }>({
    open: false,
    appId: null,
    appName: null
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await apiService.applications.list();
      // PRODUCTION CLEANUP: Removed mock data augmentation
      setApplications(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredApps = applications.filter(app => {
    const name = app.name || '';
    const id = app.id || '';
    const ownerName = app.ownerName || '';
    const matchesSearch = 
      name.toLowerCase().includes(searchQuery.toLowerCase()) || 
      id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ownerName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterStatus === 'All' || app.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const handleAssignAgent = (agentName: string) => {
    setApplications(prev => prev.map(app => 
      app.id === assignmentModal.appId 
      ? { ...app, assignedTo: agentName, status: 'Assigned' } 
      : app
    ));
    setAssignmentModal({ open: false, appId: null, appName: null });
  };

  const downloadCSV = () => {
    const headers = ['ID', 'Business Name', 'Owner', 'ID Number', 'Email', 'Phone', 'Daily Sales', 'Tier', 'Limit', 'Location', 'Status', 'Attribution'];
    const rows = filteredApps.map(app => [
      app.id,
      app.name,
      app.ownerName,
      app.idNumber,
      app.email,
      app.phone,
      app.dailySales,
      app.tier,
      app.amount,
      app.location,
      app.status,
      app.referralCode || 'Direct'
    ]);

    let csvContent = "data:text/csv;charset=utf-8," + headers.join(",") + "\n" + rows.map(e => e.join(",")).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `CHUI_Ledger_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="bg-[#f8fafc] min-h-screen p-6 md:p-12 text-left">
      <div className="max-w-[1600px] mx-auto">
        <header className="flex flex-col xl:flex-row justify-between items-start xl:items-center mb-12 gap-8">
          <div className="text-left">
            <div className="flex items-center gap-3 mb-4">
              <span className="bg-[#2d334a] text-white text-[10px] font-black px-4 py-1.5 rounded-full uppercase tracking-widest flex items-center">
                <ShieldCheck size={12} className="mr-2 text-chui-yellow" /> Executive Sales Directorate
              </span>
              <span className="bg-success/10 text-success text-[10px] font-black px-4 py-1.5 rounded-full uppercase tracking-widest border border-success/20">
                Live Funnel Tracking
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-slate-900 tracking-tighter uppercase">Intake Directorate Ledger</h1>
            <p className="text-slate-500 font-bold mt-3 uppercase text-xs tracking-widest">Global MSME Onboarding • Asset Allocation Protocol</p>
          </div>
          <div className="flex flex-wrap gap-4">
            <div className="bg-white p-2 rounded-2xl shadow-sm border border-slate-100 flex gap-2">
              {['All', 'New', 'Assigned', 'Verified'].map(status => (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status)}
                  className={`px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${filterStatus === status ? 'bg-[#2d334a] text-white shadow-lg' : 'text-slate-400 hover:bg-slate-50'}`}
                >
                  {status}
                </button>
              ))}
            </div>
            <button onClick={downloadCSV} className="bg-white text-slate-700 border-2 border-slate-100 px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-50 transition-all flex items-center shadow-sm active:scale-95">
              <Download size={16} className="mr-3" /> Export Ledger
            </button>
            <button onClick={onLogout} className="bg-red-50 text-red-600 border border-red-100 px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-red-100 transition-all flex items-center active:scale-95">
              <LogOut size={16} className="mr-3" /> Terminate Session
            </button>
          </div>
        </header>

        <div className="bg-white rounded-[3.5rem] border border-slate-200 shadow-sm overflow-hidden text-left mb-12">
          <div className="p-10 lg:p-14 border-b border-slate-50 flex flex-col md:flex-row justify-between items-start md:items-center gap-8 bg-slate-50/30">
            <div>
              <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight">Active Intake Records</h2>
              <p className="text-[11px] font-bold text-slate-400 uppercase mt-1 tracking-widest">Manage MSME Funnel Flow & Attribution.</p>
            </div>
            <div className="relative min-w-[400px]">
              <Search size={18} className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-400" />
              <input 
                type="text" 
                placeholder="Search by Business, Owner or ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-16 pr-8 py-5 bg-white border-2 border-slate-100 rounded-[2rem] text-sm font-bold w-full outline-none focus:ring-8 focus:ring-chui-navy/5 focus:border-chui-navy/20 transition-all shadow-sm"
              />
            </div>
          </div>

          <div className="overflow-x-auto no-scrollbar">
            <table className="w-full text-left min-w-[1400px]">
              <thead>
                <tr className="bg-slate-50/50">
                  <th className="px-10 py-8 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-100">MSME Applicant</th>
                  <th className="px-6 py-8 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-100">Identity & Contact</th>
                  <th className="px-6 py-8 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-100">Financial Diagnostics</th>
                  <th className="px-6 py-8 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-100">Location Metadata</th>
                  <th className="px-6 py-8 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-100">Attribution</th>
                  <th className="px-6 py-8 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-100">Status</th>
                  <th className="px-10 py-8 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-100 text-right">Directorate Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filteredApps.map((app, i) => (
                  <tr key={i} className="group hover:bg-slate-50/50 transition-all">
                    <td className="px-10 py-10">
                      <div className="flex items-center gap-5">
                        <div className="w-14 h-14 rounded-2xl bg-[#2d334a] text-chui-yellow flex items-center justify-center font-black text-lg shadow-xl shadow-slate-200">
                          {(app.name || '?')[0]}
                        </div>
                        <div>
                          <p className="text-base font-black text-slate-900 tracking-tight uppercase leading-none mb-2">{app.name}</p>
                          <div className="flex items-center gap-2">
                             <Tag size={10} className="text-slate-400" />
                             <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{app.id}</p>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-10">
                      <div className="space-y-2.5">
                        <div className="flex items-center gap-2">
                          <UserCircle size={14} className="text-slate-400" />
                          <p className="text-[11px] font-black text-slate-800 uppercase">{app.ownerName}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Hash size={12} className="text-slate-300" />
                          <p className="text-[10px] font-bold text-slate-500 uppercase">ID: {app.idNumber}</p>
                        </div>
                        <div className="flex items-center gap-4 mt-2">
                          <a href={`tel:${app.phone}`} className="p-2 bg-slate-100 rounded-lg text-slate-500 hover:bg-chui-navy hover:text-white transition-colors">
                            <Smartphone size={12} />
                          </a>
                          <a href={`mailto:${app.email}`} className="p-2 bg-slate-100 rounded-lg text-slate-500 hover:bg-chui-navy hover:text-white transition-colors">
                            <Mail size={12} />
                          </a>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-10">
                      <div className="space-y-3">
                         <div className="flex items-center gap-2">
                            <TrendingUp size={14} className="text-chui-navy" />
                            <p className="text-sm font-black text-chui-navy">KES {(app.dailySales || 0).toLocaleString()}</p>
                            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">/ Day</span>
                         </div>
                         <div className="flex items-center gap-2">
                            <CreditCard size={12} className="text-slate-400" />
                            <p className="text-[10px] font-black text-slate-600 uppercase tracking-widest">{app.tier}</p>
                         </div>
                         <div className="flex items-center gap-2">
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Limit: KES {(app.amount || 0).toLocaleString()}</p>
                         </div>
                      </div>
                    </td>
                    <td className="px-6 py-10">
                       <div className="flex items-start gap-3">
                          <MapPin size={16} className="text-slate-300 mt-0.5" />
                          <div className="max-w-[180px]">
                             <p className="text-[10px] font-black text-slate-700 uppercase leading-relaxed tracking-wider">{app.location || 'Not Recorded'}</p>
                             <button className="text-[9px] font-black text-chui-navy uppercase mt-2 hover:underline">View Map</button>
                          </div>
                       </div>
                    </td>
                    <td className="px-6 py-10">
                      {app.referralCode ? (
                        <div className="space-y-2">
                          <span className="text-[10px] font-black bg-chui-yellow/10 text-chui-navy border border-chui-yellow/20 px-3 py-1.5 rounded-lg flex items-center w-fit uppercase tracking-widest">
                            <Key size={10} className="mr-1.5" /> {app.referralCode}
                          </span>
                          {app.assignedTo && (
                            <div className="flex items-center gap-2 text-[10px] font-bold text-slate-500 uppercase">
                               <UserCheck size={12} className="text-success" />
                               {app.assignedTo.split(' ')[0]}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <span className="text-[10px] font-black text-slate-300 uppercase italic tracking-widest">Direct Funnel</span>
                          {app.assignedTo && (
                            <div className="flex items-center gap-2 text-[10px] font-bold text-slate-500 uppercase">
                               <UserCheck size={12} className="text-success" />
                               {app.assignedTo.split(' ')[0]}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-10">
                      <span className={`text-[10px] font-black px-5 py-2 rounded-full uppercase flex items-center w-fit border-2 ${
                        app.status === 'New' || !app.status ? 'bg-amber-50 text-amber-700 border-amber-100 shadow-lg shadow-amber-100/50' : 
                        app.status === 'Assigned' ? 'bg-blue-50 text-blue-700 border-blue-100' : 
                        'bg-success/10 text-success border-success/20'
                      }`}>
                        <div className={`w-2 h-2 rounded-full mr-3 ${app.status === 'New' || !app.status ? 'bg-amber-500 animate-pulse' : app.status === 'Assigned' ? 'bg-blue-500' : 'bg-success'}`}></div>
                        {app.status || 'New'}
                      </span>
                    </td>
                    <td className="px-10 py-10 text-right">
                      <div className="flex items-center justify-end gap-3">
                         <button 
                          onClick={() => setAssignmentModal({ open: true, appId: app.id, appName: app.name })}
                          className="px-6 py-3 bg-[#2d334a] text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all flex items-center shadow-xl active:scale-95"
                        >
                          <UserPlus size={14} className="mr-2 text-chui-yellow" /> Allocate
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredApps.length === 0 && (
            <div className="py-32 flex flex-col items-center justify-center text-center">
               <div className="w-20 h-20 bg-slate-50 rounded-[2rem] flex items-center justify-center text-slate-300 mb-8">
                  <Search size={40} />
               </div>
               <h3 className="text-2xl font-black text-slate-900 uppercase">Registry Empty</h3>
               <p className="text-slate-400 font-bold uppercase text-[10px] tracking-widest mt-2">The intake funnel is currently waiting for new MSME partners.</p>
            </div>
          )}
        </div>

        {assignmentModal.open && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-300">
            <div className="bg-white w-full max-w-lg rounded-[3.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-500 text-left">
              <div className="bg-[#2d334a] p-10 lg:p-12 text-white flex justify-between items-center relative">
                <div className="absolute top-0 right-0 p-12 opacity-5 pointer-events-none">
                   <UserPlus size={180} />
                </div>
                <div className="relative z-10">
                  <h3 className="text-2xl font-black uppercase tracking-tight mb-2">Directorate Allocation</h3>
                  <p className="text-[10px] font-black text-chui-yellow uppercase tracking-[0.2em]">Lead: {assignmentModal.appName}</p>
                </div>
                <button onClick={() => setAssignmentModal({ open: false, appId: null, appName: null })} className="p-4 bg-white/10 rounded-2xl hover:bg-white/20 transition-colors relative z-10">
                  <X size={20} />
                </button>
              </div>
              <div className="p-10 lg:p-12 space-y-4 max-h-[500px] overflow-y-auto no-scrollbar">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6 px-2">Select Active Field Guardian</p>
                {AVAILABLE_AGENTS.map((agent, idx) => (
                  <button 
                    key={idx}
                    onClick={() => handleAssignAgent(agent.name)}
                    className="w-full p-6 bg-slate-50 border-2 border-slate-50 rounded-[2rem] flex items-center justify-between group hover:bg-white hover:border-chui-navy hover:shadow-xl hover:shadow-chui-navy/5 transition-all"
                  >
                    <div className="flex items-center gap-5 text-left">
                      <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-chui-navy border border-slate-100 group-hover:bg-chui-navy group-hover:text-chui-yellow transition-all shadow-sm">
                        <UserCircle size={24} />
                      </div>
                      <div>
                        <p className="text-sm font-black text-slate-900 uppercase tracking-tight">{agent.name}</p>
                        <div className="flex items-center gap-2 mt-1">
                           <Key size={10} className="text-slate-400" />
                           <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.1em]">{agent.code}</p>
                        </div>
                      </div>
                    </div>
                    <div className="w-8 h-8 rounded-full bg-white border border-slate-100 flex items-center justify-center text-slate-300 group-hover:bg-chui-navy group-hover:text-white transition-all group-hover:translate-x-1">
                       <ArrowRight size={14} />
                    </div>
                  </button>
                ))}
              </div>
              <div className="p-8 bg-slate-50 border-t border-slate-100 text-center">
                 <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.4em]">Directorate Override Enabled</p>
              </div>
            </div>
          </div>
        )}

        <footer className="mt-20 flex flex-wrap justify-center gap-12 opacity-40 text-[9px] font-black uppercase tracking-[0.5em] text-slate-400 text-center">
           <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-success" />
              <span>Directorate Cloud Node: Secure</span>
           </div>
           <span>ISO 27001 Certified Intake</span>
           <span>Standardized MSME Evaluation</span>
           <span>© 2024 Ikhaya Group Limited</span>
        </footer>
      </div>
    </div>
  );
};

export default PortalAdmin;